// iter56 · Foundation Slice 1A COMPLETION PATCH — Customers list + detail.
// Adds "Add Property" and "Create Job" inline forms to the Customer detail
// page and links Job rows to the new /app/jobs/:jid detail screen.
// Preserves the existing shell; no redesign, no navigation overhaul.
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import { Plus, Search, User, Home, Briefcase, AlertTriangle, X } from "lucide-react";

export const CustomersList = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/foundation/customers")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  // V-P1 · Customers landing — recent-first (limit to 10), all
  // customers remain reachable via search. PO-approved: NO new
  // active/past/archived lifecycle architecture is introduced here.
  const ordered = [...rows].sort((a, b) => {
    const at = new Date(a.updated_at || a.created_at || 0).getTime();
    const bt = new Date(b.updated_at || b.created_at || 0).getTime();
    return bt - at;
  });
  const query = q.trim().toLowerCase();
  const filtered = query
    ? ordered.filter((c) =>
        [c.first_name, c.last_name, c.business_name, c.primary_phone, c.primary_email, c.address]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(query))
      )
    : ordered.slice(0, 10);
  const totalCount = rows.length;
  const showingCount = filtered.length;
  const hidden = !query && totalCount > 10 ? totalCount - 10 : 0;

  const initials = (c) => {
    const src =
      c.business_name ||
      `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
      "?";
    const parts = src.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
  };

  return (
    <DashboardLayout
      title={t("pages.customers.title")}
      subtitle={t("pages.customers.subtitle")}
    >
      <div data-testid="customers-page" className="space-y-3">
        <div className="jc-card p-2 flex items-center gap-2">
          <Search size={14} className="text-[var(--jc-text-soft)] ml-2" />
          <input
            type="search"
            placeholder={t("customers_form.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="customers-search-input"
            className="flex-1 px-2 py-1.5 outline-none bg-transparent text-sm"
          />
        </div>
        {!loading && totalCount > 0 && (
          <div className="flex items-center justify-between text-xs text-[var(--jc-text-soft)] px-1">
            <span>
              {query
                ? t("customers_form.showing_search", showingCount)
                : t("customers_form.showing_recent", showingCount, totalCount)}
            </span>
            {hidden > 0 && (
              <span data-testid="customers-hidden-hint">
                {t("customers_form.hidden_hint", hidden)}
              </span>
            )}
          </div>
        )}
        {loading && <div className="text-sm text-[var(--jc-text-soft)]" data-testid="customers-loading">{t("customers_form.loading")}</div>}
        {!loading && filtered.length === 0 && (
          <div className="jc-card p-4 text-sm text-[var(--jc-text-soft)]" data-testid="customers-empty">
            {t("customers_form.empty_note")}
          </div>
        )}
        <ul className="space-y-2" data-testid="customers-list">
          {filtered.map((c) => (
            <li key={c.id}>
              <Link
                to={`/app/customers/${c.id}`}
                data-testid={`customer-row-${c.id}`}
                className="jc-card p-3 flex items-center justify-between hover:bg-[var(--jc-bg)] jc-card-hover"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[var(--jc-blue)] to-[var(--jc-blue-700)] text-white flex items-center justify-center font-semibold text-xs">
                    {initials(c)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {c.business_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "(no name)"}
                    </div>
                    <div className="text-xs text-[var(--jc-text-soft)] truncate">
                      {[c.primary_phone, c.primary_email].filter(Boolean).join(" · ") || t("customers_form.no_contact_yet")}
                    </div>
                    {c.address && (
                      <div className="text-[11px] text-[var(--jc-text-soft)] truncate mt-0.5">
                        {c.address}
                      </div>
                    )}
                  </div>
                </div>
                {typeof c.project_count === "number" && c.project_count > 0 && (
                  <div className="text-[11px] font-semibold text-[var(--jc-blue)] shrink-0 ml-2">
                    {t("customers_form.project_count_short", c.project_count)}
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </DashboardLayout>
  );
};

// ------------------------------------------------------------
// Inline "Add Property" form used inside CustomerDetail. Kept
// inline (no dialog dependency) to match the existing shell.
// ------------------------------------------------------------
const AddPropertyForm = ({ customerId, onCreated, onCancel }) => {
  const [form, setForm] = useState({
    street_address: "", unit: "", city: "", state: "", zip_code: "",
    nickname: "", property_class: "residential", property_type: "",
    access_instructions: "", occupancy_status: "",
  });
  const [duplicate, setDuplicate] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (confirm = false) => {
    setSaving(true);
    try {
      const url = "/foundation/properties" + (confirm ? "?confirm_duplicate=true" : "");
      const { data } = await api.post(url, { customer_id: customerId, ...form });
      if (data.duplicate_warning) {
        setDuplicate(data.suggestions || []);
      } else {
        toast.success("Property added");
        onCreated(data);
      }
    } catch { toast.error("Could not save property"); }
    finally { setSaving(false); }
  };

  const bind = (k) => ({
    value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }),
    "data-testid": `property-new-${k.replace(/_/g, "-")}`,
    className: "w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white",
  });

  return (
    <div data-testid="property-new-form" className="jc-card p-4 space-y-3 border-l-4 border-[var(--jc-navy)]">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Add property</div>
        <button data-testid="property-new-close" onClick={onCancel} className="text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"><X size={14} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Street address</label>
          <input {...bind("street_address")} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Unit</label>
          <input {...bind("unit")} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Nickname</label>
          <input {...bind("nickname")} placeholder="e.g. Rental #2" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">City</label>
          <input {...bind("city")} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">State</label>
          <input {...bind("state")} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">ZIP</label>
          <input {...bind("zip_code")} />
        </div>
        <div>
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Class</label>
          <select {...bind("property_class")}>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Access instructions (internal only)</label>
          <input {...bind("access_instructions")} placeholder="Gate code, dog on premises, etc." />
        </div>
      </div>
      {duplicate && (
        <div data-testid="property-new-duplicate-warning" className="p-3 border-l-4 border-amber-400 bg-amber-50/50 rounded">
          <div className="text-sm font-semibold text-amber-900 flex items-center gap-1"><AlertTriangle size={14} /> Similar address already on file</div>
          <ul className="mt-1 text-xs text-amber-900 space-y-1">
            {duplicate.map((s) => <li key={s.id}>{s.street_address} {s.zip_code}</li>)}
          </ul>
          <div className="mt-2 flex gap-2">
            <button data-testid="property-new-cancel-duplicate" onClick={() => setDuplicate(null)} className="px-3 py-1.5 text-xs font-semibold rounded border border-[var(--jc-border-strong)]">Cancel</button>
            <button data-testid="property-new-confirm-duplicate" onClick={() => submit(true)} disabled={saving} className="px-3 py-1.5 text-xs font-semibold rounded bg-[var(--jc-red)] text-white">Add anyway</button>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-[var(--jc-border-strong)]" data-testid="property-new-cancel-btn">Cancel</button>
        <button
          onClick={() => submit(false)}
          disabled={saving || !form.street_address}
          data-testid="property-new-save-btn"
          className="px-4 py-1.5 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-50"
        >{saving ? "Saving…" : "Add property"}</button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// Inline "Create Job" form used inside CustomerDetail.
// ------------------------------------------------------------
const AddJobForm = ({ customerId, properties, onCreated, onCancel }) => {
  const [form, setForm] = useState({
    name: "",
    property_id: properties[0]?.id || "",
    job_type: "estimate_or_proposal",
    trade: "",
    description: "",
    customer_access_preference: "use_business_default",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/foundation/jobs", { customer_id: customerId, ...form });
      toast.success("Job created");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create job");
    } finally { setSaving(false); }
  };

  const bind = (k) => ({
    value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }),
    "data-testid": `job-new-${k.replace(/_/g, "-")}`,
    className: "w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white",
  });

  return (
    <div data-testid="job-new-form" className="jc-card p-4 space-y-3 border-l-4 border-[var(--jc-navy)]">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Create job</div>
        <button data-testid="job-new-close" onClick={onCancel} className="text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"><X size={14} /></button>
      </div>
      {properties.length === 0 ? (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="job-new-needs-property">
          Add a property first — every job must be tied to a property.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Job name</label>
            <input {...bind("name")} placeholder="e.g. Roof replacement — front slope" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Property</label>
            <select {...bind("property_id")}>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nickname || p.street_address || "(no address)"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Job type</label>
            <select {...bind("job_type")}>
              <option value="estimate_or_proposal">Estimate / Proposal</option>
              <option value="inspection">Inspection</option>
              <option value="service_or_repair">Service or Repair</option>
              <option value="active_project">Active Project</option>
              <option value="warranty_or_callback">Warranty or Callback</option>
              <option value="custom">Custom</option>
              <option value="not_determined">Not determined</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Trade</label>
            <input {...bind("trade")} placeholder="Roofing, Flooring, …" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Customer access preference</label>
            <select {...bind("customer_access_preference")}>
              <option value="use_business_default">Use business default</option>
              <option value="portal_and_email">Portal + Email</option>
              <option value="email_only">Email only</option>
              <option value="sms_only">SMS only</option>
              <option value="no_customer_access">No customer access</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">Description (internal)</label>
            <textarea rows={2} {...bind("description")} />
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-[var(--jc-border-strong)]" data-testid="job-new-cancel-btn">Cancel</button>
        <button
          onClick={submit}
          disabled={saving || !form.name.trim() || !form.property_id}
          data-testid="job-new-save-btn"
          className="px-4 py-1.5 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-50"
        >{saving ? "Saving…" : "Create job"}</button>
      </div>
    </div>
  );
};

export const CustomerDetail = () => {
  const { t } = useLang();
  const { cid } = useParams();
  // Missing Link · Stage 1 (Effective M-2). Publish the active
  // customer anchor through the existing per-request
  // workspace_context transport. Backend re-sanitizes the
  // workspace_context on receipt (allowlist scrub) and
  // materializes it onto a per-request LeviContext envelope; it
  // is NOT persisted. Stage 1 does not add a downstream anchor
  // consumer, so no tenant/durable-record ownership lookup is
  // performed against this anchor yet. Any future consumer
  // (Stage 2 · M-3) MUST validate the anchor against the
  // authenticated user's durable customer/project records
  // before trusting it as a resolved referent. The anchor is
  // ephemeral, non-authoritative, and fallback-only — explicit
  // current-turn intent always wins. Unmount clears the hint
  // via the hook's useEffect return.
  useLeviWorkspaceHint("customer_detail", {
    active_customer_id: cid,
    open_document_type: "customer",
    open_document_id: cid,
    current_section: "customer_detail",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProperty, setShowProperty] = useState(false);
  const [showJob, setShowJob] = useState(false);
  const [showFoundation, setShowFoundation] = useState(false);

  const refresh = () => {
    api.get(`/foundation/customers/${cid}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/foundation/customers/${cid}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [cid]);

  if (loading) return <DashboardLayout title={t("pages.customers.detail_loading_title")} subtitle={t("pages.customers.detail_loading_subtitle")}><div data-testid="customer-detail-loading" /></DashboardLayout>;
  if (!data) return (
    <DashboardLayout title={t("pages.customers.detail_loading_title")} subtitle={t("pages.customers.detail_not_found_subtitle")}>
      <div data-testid="customer-detail-notfound" className="jc-card p-4">This customer isn’t available.</div>
    </DashboardLayout>
  );

  const properties = data.properties || [];
  const jobs = data.jobs || [];
  const projects = data.projects || [];

  const displayName =
    data.business_name ||
    `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
    "Customer";
  const initialsRaw = displayName.split(/\s+/).filter(Boolean);
  const initials = ((initialsRaw[0]?.[0] || "?") + (initialsRaw[1]?.[0] || "")).toUpperCase();
  const yearOnly = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      return d.getUTCFullYear();
    } catch { return null; }
  };
  const sinceYear = yearOnly(data.created_at);
  const contactBits = [
    data.address,
    data.primary_phone,
    data.primary_email,
  ].filter(Boolean);

  const statusForProject = (p) => {
    const s = (p.status || "").toLowerCase();
    if (s === "draft") return { cls: "vp1-pill vp1-pill-amber", label: "Draft" };
    if (s === "sent" || s === "proposal_sent") return { cls: "vp1-pill vp1-pill-slate", label: "Proposal sent" };
    if (s === "under_contract" || s === "signed" || s === "contract") return { cls: "vp1-pill vp1-pill-green", label: "Under contract" };
    if (s === "scheduled") return { cls: "vp1-pill vp1-pill-blue", label: "Scheduled" };
    if (s === "in_progress" || s === "active") return { cls: "vp1-pill vp1-pill-blue", label: "In progress" };
    if (s === "invoiced") return { cls: "vp1-pill vp1-pill-green", label: "Invoiced" };
    if (s === "closed" || s === "completed" || s === "warranty") {
      const yr = yearOnly(p.updated_at || p.created_at);
      return { cls: "vp1-pill vp1-pill-slate", label: yr ? `Completed · ${yr}` : "Completed" };
    }
    if (s === "consultation") return { cls: "vp1-pill vp1-pill-teal", label: "Consultation" };
    return { cls: "vp1-pill vp1-pill-slate", label: (p.status || "—").replace(/_/g, " ") };
  };

  return (
    <DashboardLayout
      title={displayName}
      subtitle={[data.primary_phone, data.primary_email].filter(Boolean).join(" · ")}
    >
      <div data-testid="customer-detail-page" className="space-y-6 max-w-5xl">
        {/* V-P1 · S3 header: identity + Ask Levi */}
        <div className="flex items-start gap-6" data-testid="customer-detail-header">
          <div className="shrink-0 w-16 h-16 rounded-full bg-[var(--jc-navy)] text-white flex items-center justify-center text-xl font-bold" data-testid="customer-detail-avatar">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            {sinceYear && (
              <div className="overline" data-testid="customer-since">Customer since {sinceYear}</div>
            )}
            <h1 className="font-display text-3xl" data-testid="customer-detail-name">{displayName}</h1>
            <p className="text-sm text-[var(--jc-text-soft)] truncate" data-testid="customer-detail-contact">
              {contactBits.length > 0 ? contactBits.join(" · ") : t("customers_form.no_contact_yet")}
            </p>
          </div>
          <Link
            to={`/app/levi?about=customer:${cid}`}
            data-testid="ask-levi-about-customer-btn"
            className="jc-btn-ghost text-sm shrink-0"
          >
            Ask Levi about {(data.first_name || displayName).split(" ")[0]}
          </Link>
        </div>

        {/* V-P1 · S3 projects card — real history from db.projects */}
        <section className="jc-card p-6" data-testid="customer-projects-section">
          <div className="overline mb-3">Projects ({projects.length})</div>
          {projects.length === 0 ? (
            <div className="text-sm text-[var(--jc-text-soft)]" data-testid="customer-projects-empty">
              No projects yet for this customer.
            </div>
          ) : (
            <div className="divide-y divide-[var(--jc-border)]" data-testid="customer-projects-list">
              {projects.map((p) => {
                const s = statusForProject(p);
                const title = p.job_type || p.project_name || p.job_address || "Project";
                const sub = [p.job_address, p.customer_name].filter(Boolean).join(" · ");
                return (
                  <Link
                    key={p.id}
                    to={`/app/projects/${p.id}`}
                    data-testid={`customer-project-row-${p.id}`}
                    className="py-4 flex items-center gap-4 hover:bg-[var(--jc-bg)] px-1 -mx-1 rounded"
                  >
                    <span className={s.cls}>{s.label}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{title}</div>
                      {sub && (
                        <div className="text-xs text-[var(--jc-text-soft)] truncate">{sub}</div>
                      )}
                    </div>
                    <span className="text-sm text-[var(--jc-blue-700)] font-semibold shrink-0">Open →</span>
                  </Link>
                );
              })}
            </div>
          )}
          <p className="text-xs text-[var(--jc-text-soft)] italic mt-3">
            Completed projects stay here forever. Ask Levi anytime: “Pull up {(data.first_name || displayName).split(" ")[0]} — I need the warranty documents.”
          </p>
        </section>

        {/* Preserved Foundation (Properties + Jobs) — collapsed to keep V-P1 focus on real history */}
        <section data-testid="customer-foundation-section">
          <button
            type="button"
            onClick={() => setShowFoundation((v) => !v)}
            data-testid="customer-foundation-toggle"
            className="text-xs font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1.5"
          >
            {showFoundation ? "▼" : "▶"} Foundation records · Properties ({properties.length}) · Jobs ({jobs.length})
          </button>
          {showFoundation && (
            <div className="mt-3 space-y-4">
              <div className="jc-card p-4" data-testid="customer-properties">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Home size={14} /> Properties ({properties.length})
                  </div>
                  {!showProperty && (
                    <button
                      data-testid="add-property-btn"
                      onClick={() => setShowProperty(true)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                    >
                      <Plus size={12} /> Add property
                    </button>
                  )}
                </div>
                {showProperty && (
                  <div className="mb-3">
                    <AddPropertyForm
                      customerId={cid}
                      onCreated={() => { setShowProperty(false); refresh(); }}
                      onCancel={() => setShowProperty(false)}
                    />
                  </div>
                )}
                <ul className="space-y-1.5" data-testid="customer-properties-list">
                  {properties.map((p) => (
                    <li key={p.id} className="text-sm flex items-center justify-between" data-testid={`property-row-${p.id}`}>
                      <div>
                        <span className="font-medium">{p.nickname || p.street_address || "(no address)"}</span>
                        <span className="text-xs text-[var(--jc-text-soft)]"> · {[p.city, p.state, p.zip_code].filter(Boolean).join(" ")}</span>
                      </div>
                      <span className="text-xs text-[var(--jc-text-soft)] capitalize">{p.property_class}</span>
                    </li>
                  ))}
                  {properties.length === 0 && <li className="text-xs text-[var(--jc-text-soft)]" data-testid="properties-empty">No properties yet.</li>}
                </ul>
              </div>

              <div className="jc-card p-4" data-testid="customer-jobs">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Briefcase size={14} /> Jobs ({jobs.length})
                  </div>
                  {!showJob && (
                    <button
                      data-testid="create-job-btn"
                      onClick={() => setShowJob(true)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                    >
                      <Plus size={12} /> Create job
                    </button>
                  )}
                </div>
                {showJob && (
                  <div className="mb-3">
                    <AddJobForm
                      customerId={cid}
                      properties={properties}
                      onCreated={() => { setShowJob(false); refresh(); }}
                      onCancel={() => setShowJob(false)}
                    />
                  </div>
                )}
                <ul className="space-y-1.5" data-testid="customer-jobs-list">
                  {jobs.map((j) => (
                    <li key={j.id} data-testid={`job-row-${j.id}`}>
                      <Link
                        to={`/app/jobs/${j.id}`}
                        className="flex items-center justify-between text-sm hover:bg-[var(--jc-bg)] px-1 py-1 rounded"
                      >
                        <div>
                          <span className="font-medium">{j.name}</span>
                          <span className="text-xs text-[var(--jc-text-soft)]"> · {j.job_type?.replace(/_/g, " ")}</span>
                        </div>
                        <span className="text-xs text-[var(--jc-text-soft)] capitalize">{j.status}</span>
                      </Link>
                    </li>
                  ))}
                  {jobs.length === 0 && <li className="text-xs text-[var(--jc-text-soft)]" data-testid="jobs-empty">No jobs yet.</li>}
                </ul>
              </div>
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

export const CustomerNew = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  // R-25 · Prefill from URL query. When Levi's grounded intake
  // confirmation navigates here (`/app/customers/new?name=John
  // Martinez&address=...&phone=...&email=...`), populate the form
  // so the contractor only reviews + saves. Read once on mount;
  // NEVER auto-submit — Save remains contractor-driven.
  const [searchParams] = useSearchParams();
  const _prefill = React.useMemo(() => {
    const _name = (searchParams.get("name") || "").trim();
    const [_first, ..._rest] = _name.split(/\s+/);
    return {
      first_name: _first || "",
      last_name: _rest.join(" ").trim(),
      primary_phone: (searchParams.get("phone") || "").trim(),
      primary_email: (searchParams.get("email") || "").trim(),
    };
  }, [searchParams]);
  const [form, setForm] = useState({
    customer_type: "individual",
    first_name: _prefill.first_name,
    last_name: _prefill.last_name,
    business_name: "",
    primary_phone: _prefill.primary_phone,
    primary_email: _prefill.primary_email,
    preferred_contact_method: "phone", preferred_language: "en",
  });
  const [duplicate, setDuplicate] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (confirm = false) => {
    setSaving(true);
    try {
      const url = "/foundation/customers" + (confirm ? "?confirm_duplicate=true" : "");
      const { data } = await api.post(url, form);
      if (data.duplicate_warning) {
        setDuplicate(data.suggestions || []);
      } else {
        toast.success("Customer created");
        navigate(`/app/customers/${data.id}`);
      }
    } catch { toast.error("Could not save customer"); }
    finally { setSaving(false); }
  };

  const bind = (k) => ({
    value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }),
    "data-testid": `customer-new-${k.replace(/_/g, "-")}`,
  });

  return (
    <DashboardLayout title={t("pages.customers.new_title")} subtitle={t("pages.customers.new_subtitle")}>
      <div data-testid="customer-new-page" className="max-w-2xl space-y-3">
        <div className="jc-card p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("customers_form.type_label")}</label>
            <select {...bind("customer_type")} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)]">
              <option value="individual">{t("customers_form.individual")}</option>
              <option value="business">{t("customers_form.business")}</option>
            </select>
          </div>
          {form.customer_type === "business" ? (
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("customers_form.business_name")}</label>
              <input {...bind("business_name")} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)]" />
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("customers_form.first_name")}</label>
                <input {...bind("first_name")} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)]" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("customers_form.last_name")}</label>
                <input {...bind("last_name")} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)]" />
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("customers_form.phone")}</label>
            <input {...bind("primary_phone")} className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("customers_form.email")}</label>
            <input {...bind("primary_email")} type="email" className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)]" />
          </div>
        </div>
        {duplicate && (
          <div data-testid="customer-new-duplicate-warning" className="jc-card p-3 border-l-4 border-amber-400 bg-amber-50/50">
            <div className="text-sm font-semibold text-amber-900 flex items-center gap-1">
              <AlertTriangle size={14} /> Possible duplicate found
            </div>
            <ul className="mt-1 text-xs text-amber-900 space-y-1">
              {duplicate.map((s) => (
                <li key={s.id}>
                  <Link to={`/app/customers/${s.id}`} className="underline">
                    {s.business_name || `${s.first_name || ""} ${s.last_name || ""}`.trim()}
                  </Link>
                  <span className="text-amber-800"> · {[s.primary_phone, s.primary_email].filter(Boolean).join(" / ")}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex gap-2">
              <button
                data-testid="customer-new-cancel-duplicate"
                onClick={() => setDuplicate(null)}
                className="px-3 py-1.5 text-xs font-semibold rounded border border-[var(--jc-border-strong)]"
              >Cancel</button>
              <button
                data-testid="customer-new-confirm-duplicate"
                onClick={() => submit(true)}
                disabled={saving}
                className="px-3 py-1.5 text-xs font-semibold rounded bg-[var(--jc-red)] text-white"
              >Create anyway</button>
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button
            onClick={() => submit(false)}
            disabled={saving || (form.customer_type === "individual" && !form.first_name && !form.last_name) || (form.customer_type === "business" && !form.business_name)}
            data-testid="customer-new-save-btn"
            className="px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-50"
          >{saving ? t("customers_form.saving") : t("customers_form.save_customer")}</button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomersList;
